import { Meta } from "@solidjs/meta";
import { type RouteSectionProps, useLocation } from "@solidjs/router";
import { Show } from "solid-js";
import { MarketingLayout } from "~/components/marketing/MarketingLayout";
import { marketingCard } from "~/components/marketing/sections";
import { useI18n } from "~/i18n";
import { getAppBaseUrl } from "~/lib/getAppBaseUrl";
import { getSocialCardImageUrl } from "~/lib/socialCard";

export default function MarketingRouteLayout(props: RouteSectionProps) {
  const { t } = useI18n();
  const location = useLocation();
  // The one place a marketing page or ?s= link picks its card image and og:title. The bare home page
  // and the blog and changelog indexes write their own og:title, so they get no name here.
  const card = () => marketingCard(location.pathname, location.query.s);
  const imageUrl = () => {
    const image = card()?.image;
    return image ? `${getAppBaseUrl()}/og/cards/${image}.png` : getSocialCardImageUrl(getAppBaseUrl());
  };
  const title = () => {
    const c = card();
    if (!c?.name) return undefined;
    return c.page
      ? t("marketing.card.section_title", { page: t(c.page), name: t(c.name) })
      : t("marketing.card.title", { name: t(c.name) });
  };
  // Every page under /blog/ and /changelog/ sets its own image. Decided from the path because
  // looking the slug up would pull every post and entry into every marketing page.
  const setsOwnImage = () => /^\/(blog|changelog)\/[^/]+/.test(location.pathname);
  return (
    <MarketingLayout>
      {/* solid-meta dedupes on content too, so a route that sets its own image must not also get the default. */}
      <Show when={!setsOwnImage()}>
        <Meta property="og:image" content={imageUrl()} />
        <Meta name="twitter:image" content={imageUrl()} />
      </Show>
      <Show when={title()}>
        {(cardTitle) => (
          <>
            <Meta property="og:title" content={cardTitle()} />
            <Meta name="twitter:title" content={cardTitle()} />
          </>
        )}
      </Show>
      {/* og:url keeps a ?s= link's ?s=, so an unfurler that follows og:url still gets the section's
          card. Its canonical to the bare page is in entry-server.tsx. */}
      <Show when={card()?.linked && typeof location.query.s === "string" ? location.query.s : undefined}>
        {(section) => <Meta property="og:url" content={`${getAppBaseUrl()}${location.pathname}?s=${section()}`} />}
      </Show>
      <Meta name="twitter:card" content="summary_large_image" />
      <Meta property="og:site_name" content="Chief" />
      {props.children}
    </MarketingLayout>
  );
}
